import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { Building2, Phone, Mail, Users } from "lucide-react";

interface ContactUser {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  email: string;
}

interface BranchGroup {
  branch: { id: string; name: string } | null;
  users: ContactUser[];
}

function fetchContacts(): Promise<BranchGroup[]> {
  return apiClient
    .get<{ data: BranchGroup[] }>("/auth/contacts/")
    .then((r) => r.data.data);
}

function BranchSection({ group }: { group: BranchGroup }) {
  const branchName = group.branch?.name ?? "Brak oddziału";
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Building2 size={15} className="text-[var(--color-primary)]" />
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
          {branchName}
        </h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          ({group.users.length})
        </span>
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 text-left font-medium w-64">Osoba</th>
              <th className="px-4 py-2 text-left font-medium">Telefon</th>
              <th className="px-4 py-2 text-left font-medium">Email</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
            {group.users.map((u) => (
              <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                  {u.full_name}
                </td>
                <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                  {u.phone ? (
                    <a
                      href={`tel:${u.phone}`}
                      className="inline-flex items-center gap-1.5 hover:text-[var(--color-primary)] transition-colors"
                    >
                      <Phone size={13} className="shrink-0" />
                      {u.phone}
                    </a>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-600 italic text-xs">brak</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                  {u.email ? (
                    <a
                      href={`mailto:${u.email}`}
                      className="inline-flex items-center gap-1.5 hover:text-[var(--color-primary)] transition-colors"
                    >
                      <Mail size={13} className="shrink-0" />
                      {u.email}
                    </a>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-600 italic text-xs">brak</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {group.users.map((u) => (
          <div
            key={u.id}
            className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3"
          >
            <p className="font-medium text-gray-900 dark:text-gray-100 mb-1.5">{u.full_name}</p>
            <div className="space-y-1 text-sm">
              {u.phone ? (
                <a
                  href={`tel:${u.phone}`}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-[var(--color-primary)]"
                >
                  <Phone size={13} className="shrink-0" />
                  {u.phone}
                </a>
              ) : (
                <span className="flex items-center gap-2 text-gray-400 dark:text-gray-600 italic text-xs">
                  <Phone size={13} className="shrink-0" />
                  brak telefonu
                </span>
              )}
              {u.email ? (
                <a
                  href={`mailto:${u.email}`}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-[var(--color-primary)]"
                >
                  <Mail size={13} className="shrink-0" />
                  {u.email}
                </a>
              ) : (
                <span className="flex items-center gap-2 text-gray-400 dark:text-gray-600 italic text-xs">
                  <Mail size={13} className="shrink-0" />
                  brak adresu
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ContactsPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["contacts"],
    queryFn: fetchContacts,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Users size={20} className="text-[var(--color-primary)]" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Kontakty</h1>
      </div>

      {isLoading && (
        <div className="text-sm text-gray-500 dark:text-gray-400">Ładowanie...</div>
      )}

      {isError && (
        <div className="text-sm text-red-600 dark:text-red-400">
          Nie udało się załadować kontaktów.
        </div>
      )}

      {data && data.length === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400">Brak danych.</div>
      )}

      {data?.map((group, i) => (
        <BranchSection key={group.branch?.id ?? `no-branch-${i}`} group={group} />
      ))}
    </div>
  );
}
